import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import {
  BarChart3, TrendingUp, TrendingDown, Users, Heart, DollarSign,
  Calendar, Target, Award, Activity, RefreshCw, Download, Filter,
  ChevronUp, ChevronDown, Gift, Mail, CheckCircle, XCircle, Clock,
  Building2, Megaphone, ArrowRight, PieChart
} from 'lucide-react';
import { toast } from 'sonner';

export default function DoadoresAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('30d');
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    carregarAnalytics();
  }, [periodo]);

  const carregarAnalytics = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/doadores/analytics?periodo=${periodo}`);
      setAnalytics(res.data);
    } catch (error) {
      console.error('Erro ao carregar analytics:', error);
      toast.error('Erro ao carregar analytics');
      // Set mock data for demonstration
      setAnalytics(getMockAnalytics());
    } finally {
      setLoading(false);
    }
  };

  const getMockAnalytics = () => ({
    resumo: {
      total_doadores: 156,
      novos_doadores: 12,
      doadores_ativos: 89,
      total_doacoes: 234,
      valor_total: 45670.50,
      ticket_medio: 195.17,
      taxa_retencao: 67.5,
      taxa_conversao: 23.4
    },
    evolucao: {
      labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
      doadores: [45, 52, 61, 78, 95, 112],
      doacoes: [12500, 15600, 18900, 22400, 28500, 35200],
      novos: [8, 12, 15, 18, 22, 25]
    },
    por_tipo: {
      'Empresa': { count: 45, valor: 28500, percent: 62.4 },
      'Individual': { count: 78, valor: 12200, percent: 26.7 },
      'Fundação': { count: 12, valor: 3800, percent: 8.3 },
      'Associação': { count: 8, valor: 980, percent: 2.1 },
      'Outro': { count: 2, valor: 190, percent: 0.4 }
    },
    por_distrito: [
      { distrito: 'Lisboa', doadores: 45, valor: 18500 },
      { distrito: 'Porto', doadores: 32, valor: 12300 },
      { distrito: 'Braga', doadores: 18, valor: 5600 },
      { distrito: 'Setúbal', doadores: 15, valor: 4200 },
      { distrito: 'Coimbra', doadores: 12, valor: 3100 }
    ],
    top_doadores: [
      { nome: 'Empresa ABC, Lda', tipo: 'Empresa', total: 8500, doacoes: 12 },
      { nome: 'Fundação XYZ', tipo: 'Fundação', total: 5200, doacoes: 4 },
      { nome: 'João Silva', tipo: 'Individual', total: 2800, doacoes: 24 },
      { nome: 'SuperMercado 123', tipo: 'Empresa', total: 2400, doacoes: 8 },
      { nome: 'Maria Santos', tipo: 'Individual', total: 1800, doacoes: 18 }
    ],
    campanhas: {
      total: 8,
      ativas: 3,
      concluidas: 4,
      valor_angariado: 28500,
      meta_total: 45000,
      taxa_sucesso: 72.5
    },
    propostas: {
      total: 45,
      pendentes: 5,
      aprovadas: 32,
      rejeitadas: 6,
      expiradas: 2,
      taxa_aprovacao: 84.2,
      tempo_medio_aprovacao: 4.5
    }
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const getChangeIcon = (value) => {
    if (value > 0) return <ChevronUp className="w-4 h-4 text-green-600" />;
    if (value < 0) return <ChevronDown className="w-4 h-4 text-red-600" />;
    return null;
  };

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="doadores-analytics-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-purple-500" />
            Analytics de Doadores
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Métricas e indicadores de performance do sistema de doadores
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Últimos 7 dias</SelectItem>
              <SelectItem value="30d">Últimos 30 dias</SelectItem>
              <SelectItem value="90d">Últimos 90 dias</SelectItem>
              <SelectItem value="365d">Último ano</SelectItem>
              <SelectItem value="all">Todo o período</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={carregarAnalytics}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-blue-700 dark:text-blue-300">Total Doadores</p>
                <p className="text-3xl font-bold text-blue-900 dark:text-blue-100">
                  {analytics?.resumo?.total_doadores || 0}
                </p>
                <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  +{analytics?.resumo?.novos_doadores || 0} este mês
                </p>
              </div>
              <Users className="w-8 h-8 text-blue-500 opacity-70" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200">
          <CardContent className="pt-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-green-700 dark:text-green-300">Valor Angariado</p>
                <p className="text-3xl font-bold text-green-900 dark:text-green-100">
                  {formatCurrency(analytics?.resumo?.valor_total)}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Ticket médio: {formatCurrency(analytics?.resumo?.ticket_medio)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500 opacity-70" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-purple-700 dark:text-purple-300">Taxa Retenção</p>
                <p className="text-3xl font-bold text-purple-900 dark:text-purple-100">
                  {formatPercent(analytics?.resumo?.taxa_retencao)}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  {analytics?.resumo?.doadores_ativos || 0} ativos
                </p>
              </div>
              <Activity className="w-8 h-8 text-purple-500 opacity-70" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-amber-700 dark:text-amber-300">Taxa Conversão</p>
                <p className="text-3xl font-bold text-amber-900 dark:text-amber-100">
                  {formatPercent(analytics?.resumo?.taxa_conversao)}
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Visitantes → Doadores
                </p>
              </div>
              <Target className="w-8 h-8 text-amber-500 opacity-70" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs de Analytics */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="doadores">Doadores</TabsTrigger>
          <TabsTrigger value="campanhas">Campanhas</TabsTrigger>
          <TabsTrigger value="propostas">Propostas</TabsTrigger>
        </TabsList>

        {/* Tab Overview */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Doações por Tipo */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5" />
                  Doações por Tipo de Doador
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(analytics?.por_tipo || {}).map(([tipo, data]) => (
                    <div key={tipo}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">{tipo}</span>
                        <span className="text-slate-500">
                          {data.count} doadores • {formatCurrency(data.valor)}
                        </span>
                      </div>
                      <Progress value={data.percent} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Distritos */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Top 5 Distritos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(analytics?.por_distrito || []).map((d, idx) => (
                    <div key={d.distrito} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        idx === 0 ? 'bg-amber-100 text-amber-700' :
                        idx === 1 ? 'bg-slate-100 text-slate-700' :
                        idx === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-slate-50 text-slate-500'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{d.distrito}</p>
                        <p className="text-xs text-slate-500">
                          {d.doadores} doadores
                        </p>
                      </div>
                      <p className="font-bold text-green-600">
                        {formatCurrency(d.valor)}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Doadores */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Top 5 Doadores
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-5 gap-4">
                {(analytics?.top_doadores || []).map((d, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${
                    idx === 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {idx === 0 && <Award className="w-4 h-4 text-amber-500" />}
                      <Badge variant="outline" className="text-xs">{d.tipo}</Badge>
                    </div>
                    <p className="font-medium text-sm truncate" title={d.nome}>{d.nome}</p>
                    <p className="text-lg font-bold text-green-600 mt-1">
                      {formatCurrency(d.total)}
                    </p>
                    <p className="text-xs text-slate-500">{d.doacoes} doações</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Doadores */}
        <TabsContent value="doadores" className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <Users className="w-12 h-12 mx-auto mb-3 text-blue-500 opacity-70" />
                  <p className="text-3xl font-bold">{analytics?.resumo?.total_doadores || 0}</p>
                  <p className="text-slate-500">Total de Doadores</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <TrendingUp className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-70" />
                  <p className="text-3xl font-bold text-green-600">+{analytics?.resumo?.novos_doadores || 0}</p>
                  <p className="text-slate-500">Novos Este Período</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-center">
                  <Activity className="w-12 h-12 mx-auto mb-3 text-purple-500 opacity-70" />
                  <p className="text-3xl font-bold text-purple-600">{analytics?.resumo?.doadores_ativos || 0}</p>
                  <p className="text-slate-500">Doadores Ativos</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Distribuição por Tipo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-5 gap-4">
                {Object.entries(analytics?.por_tipo || {}).map(([tipo, data]) => (
                  <div key={tipo} className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold">{data.count}</p>
                    <p className="text-sm text-slate-600">{tipo}</p>
                    <p className="text-xs text-green-600 mt-1">{formatCurrency(data.valor)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Campanhas */}
        <TabsContent value="campanhas" className="space-y-4">
          <div className="grid md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4 text-center">
                <Megaphone className="w-10 h-10 mx-auto mb-2 text-purple-500 opacity-70" />
                <p className="text-2xl font-bold">{analytics?.campanhas?.total || 0}</p>
                <p className="text-sm text-slate-500">Total Campanhas</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-4 text-center">
                <Activity className="w-10 h-10 mx-auto mb-2 text-green-600" />
                <p className="text-2xl font-bold text-green-700">{analytics?.campanhas?.ativas || 0}</p>
                <p className="text-sm text-green-600">Ativas</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <DollarSign className="w-10 h-10 mx-auto mb-2 text-green-500 opacity-70" />
                <p className="text-2xl font-bold text-green-600">{formatCurrency(analytics?.campanhas?.valor_angariado)}</p>
                <p className="text-sm text-slate-500">Angariado</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <Target className="w-10 h-10 mx-auto mb-2 text-amber-500 opacity-70" />
                <p className="text-2xl font-bold text-amber-600">{formatPercent(analytics?.campanhas?.taxa_sucesso)}</p>
                <p className="text-sm text-slate-500">Taxa de Sucesso</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Progresso das Campanhas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between text-sm mb-1">
                  <span>Valor Angariado vs Meta</span>
                  <span className="font-medium">
                    {formatCurrency(analytics?.campanhas?.valor_angariado)} / {formatCurrency(analytics?.campanhas?.meta_total)}
                  </span>
                </div>
                <Progress 
                  value={(analytics?.campanhas?.valor_angariado / analytics?.campanhas?.meta_total) * 100 || 0} 
                  className="h-4"
                />
                <div className="grid grid-cols-3 gap-4 mt-4 text-center">
                  <div className="p-3 bg-green-50 rounded">
                    <p className="font-bold text-green-700">{analytics?.campanhas?.concluidas || 0}</p>
                    <p className="text-xs text-green-600">Concluídas</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded">
                    <p className="font-bold text-blue-700">{analytics?.campanhas?.ativas || 0}</p>
                    <p className="text-xs text-blue-600">Em Curso</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded">
                    <p className="font-bold text-slate-700">{(analytics?.campanhas?.total || 0) - (analytics?.campanhas?.ativas || 0) - (analytics?.campanhas?.concluidas || 0)}</p>
                    <p className="text-xs text-slate-600">Rascunho</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Propostas */}
        <TabsContent value="propostas" className="space-y-4">
          <div className="grid md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4 text-center">
                <Mail className="w-10 h-10 mx-auto mb-2 text-blue-500 opacity-70" />
                <p className="text-2xl font-bold">{analytics?.propostas?.total || 0}</p>
                <p className="text-sm text-slate-500">Total Propostas</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-4 text-center">
                <Clock className="w-10 h-10 mx-auto mb-2 text-amber-600" />
                <p className="text-2xl font-bold text-amber-700">{analytics?.propostas?.pendentes || 0}</p>
                <p className="text-sm text-amber-600">Pendentes</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-4 text-center">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-600" />
                <p className="text-2xl font-bold text-green-700">{analytics?.propostas?.aprovadas || 0}</p>
                <p className="text-sm text-green-600">Aprovadas</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <Activity className="w-10 h-10 mx-auto mb-2 text-purple-500 opacity-70" />
                <p className="text-2xl font-bold text-purple-600">{formatPercent(analytics?.propostas?.taxa_aprovacao)}</p>
                <p className="text-sm text-slate-500">Taxa Aprovação</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Distribuição de Propostas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-5 gap-4">
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <Clock className="w-6 h-6 mx-auto mb-2 text-amber-600" />
                  <p className="text-xl font-bold text-amber-700">{analytics?.propostas?.pendentes || 0}</p>
                  <p className="text-xs text-amber-600">Pendentes</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-600" />
                  <p className="text-xl font-bold text-green-700">{analytics?.propostas?.aprovadas || 0}</p>
                  <p className="text-xs text-green-600">Aprovadas</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <XCircle className="w-6 h-6 mx-auto mb-2 text-red-600" />
                  <p className="text-xl font-bold text-red-700">{analytics?.propostas?.rejeitadas || 0}</p>
                  <p className="text-xs text-red-600">Rejeitadas</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg text-center">
                  <Calendar className="w-6 h-6 mx-auto mb-2 text-slate-500" />
                  <p className="text-xl font-bold text-slate-700">{analytics?.propostas?.expiradas || 0}</p>
                  <p className="text-xs text-slate-500">Expiradas</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <Activity className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                  <p className="text-xl font-bold text-blue-700">{analytics?.propostas?.tempo_medio_aprovacao || 0}h</p>
                  <p className="text-xs text-blue-600">Tempo Médio</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
